<template>
  <div class="d3-chart-wrapper" style="position: relative;">
    <chart-toolbar
      :locked="!!lockedY"
      :zoomed="!!zoomRange"
      :lockable="!!tkCurrentDomains"
      @lock="tkToggleLockY(tkCurrentDomains)"
      @reset="tkResetZoom()"
      @png="tkExportPng('rpm-fuel-speed')"
      @csv="tkExportCsvNow()"
    />
    <div ref="chartContainer" class="chart-container"></div>
    <div ref="tooltip" class="d3-tooltip" style="opacity: 0; pointer-events: none; position: absolute; background: rgba(0, 0, 0, 0.8); color: #fff; padding: 8px; border-radius: 4px; font-size: 12px; z-index: 10;"></div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { formatDateTime } from '@/util/date';
import { CHART_AXIS, CHART_TOOLTIP } from '@/util/apiDatetime';
import { getRpmTypeLabel } from '@/util/ship/fms-summary';
import { prettyLabel } from '@/util/ship/vesselDetail';
import { kmhToKnot } from '@/util/units';
import { interpolateNulls } from '@/util/summary/series';
import chartToolkit from './chartToolkit';
import ChartToolbar from './ChartToolbar.vue';

export default {
  name: 'RpmFuelSpeed',
  mixins: [chartToolkit],
  components: { ChartToolbar },
  props: {
    series: {
      type: Array,
      required: true,
    },
    deviceStatus: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  data() {
    return {
      resizeObserver: null,
      tkCurrentDomains: null,
    };
  },
  created() {
    this.tkLast = null;
  },
  mounted() {
    this.tkBind();
    this.drawChart();
    this.resizeObserver = new ResizeObserver(() => this.drawChart());
    this.resizeObserver.observe(this.$el);
  },
  beforeDestroy() {
    this.tkUnbind();
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  watch: {
    series: { deep: true, handler: 'drawChart' },
    deviceStatus: { deep: true, handler: 'drawChart' },
  },
  methods: {
    // Reset mengembalikan KETIGA chart, bukan cuma yang ini.
    tkResetZoom() {
      this.tkPublishZoom(null);
    },
    tkExportCsvNow() {
      if (!this.tkLast) return;
      this.tkExportCsv('rpm-fuel-speed', this.tkLast.timestamps, this.tkLast.datasets);
    },

    processData() {
      if (!this.series) return null;

      const mapData = (path) => this.series.map((data) => {
        const keys = path.split('.');
        let val = data;
        // eslint-disable-next-line no-restricted-syntax
        for (const key of keys) {
          if (val && Object.prototype.hasOwnProperty.call(val, key)) {
            val = val[key];
          } else {
            val = null;
            break;
          }
        }
        if (val === null || val === undefined) return null;
        const num = Number(val);
        return Number.isFinite(num) ? Number(num.toFixed(2)) : null;
      });

      const timestamps = this.series.map((d) => new Date(d.timestamp * 1000));
      const datasets = [];

      // 1. Speed (always present)
      const speedData = mapData('gpsData.speed').map((val) => (val === null ? null : Number(kmhToKnot(val).toFixed(2))));
      datasets.push({
        name: 'Speed (kn)',
        data: speedData,
        interpolated: interpolateNulls(speedData),
        color: '#F59E0B',
        axis: 'speed',
      });

      // 2. RPM Data (based on isSingleMainEngine and deviceStatus)
      const rpmTypes = this.deviceStatus?.rpmTypes || [];
      const rpmColors = {
        port: '#8B5CF6', starboard: '#EC4899', main: '#8B5CF6', center: '#8B5CF6',
      };

      rpmTypes.forEach((type) => {
        const rpmData = mapData(`rpmData.${type.toUpperCase()}.rpm`);
        const rpmMax = mapData(`rpmData.${type.toUpperCase()}.rpmMax`);
        const displayLabel = getRpmTypeLabel(type, rpmTypes);
        datasets.push({
          name: `${displayLabel.charAt(0).toUpperCase() + displayLabel.slice(1)} RPM`,
          data: rpmData,
          interpolated: interpolateNulls(rpmData),
          color: rpmColors[type.toLowerCase()] || '#000000',
          axis: 'rpm',
          peak: rpmMax.some((v) => v !== null) ? rpmMax : null,
        });
      });

      // 3. Fuel Data — label-driven groups (tak terbatas posisi engine; bunker sudah dikecualikan)
      const fuelGroups = (this.deviceStatus?.flowmeterGroups || []).map((g) => g.name);
      const fuelColors = {
        PORT: '#3B82F6', STARBOARD: '#06B6D4', CENTER: '#3B82F6',
      };
      const fuelPalette = ['#3B82F6', '#06B6D4', '#10B981', '#6366F1', '#F97316', '#14B8A6'];

      fuelGroups.forEach((name, idx) => {
        const fuelData = mapData(`fmData.${name}_FuelCons`);
        const hasValidData = fuelData.some((val) => val !== null);

        if (hasValidData) {
          datasets.push({
            name: `${prettyLabel(name)} Fuel (L/h)`,
            data: fuelData,
            interpolated: interpolateNulls(fuelData),
            color: fuelColors[name.toUpperCase()] || fuelPalette[idx % fuelPalette.length],
            axis: 'fuel',
          });
        }
      });

      return { timestamps, datasets };
    },

    drawChart() {
      const processed = this.processData();
      if (!processed) return;
      const { timestamps, datasets } = processed;
      const visible = this.tkVisible(datasets);
      const container = this.$refs.chartContainer;

      d3.select(container).selectAll('*').remove();

      const visibleWidth = this.$el.clientWidth || container.clientWidth;
      const isCompact = visibleWidth < 576;
      const chartOuterWidth = Math.max(container.clientWidth, isCompact ? 720 : 0);

      // Margin kanan desktop diseragamkan biar crosshair antar chart sejajar.
      const margin = {
        top: isCompact ? 42 : 28,
        right: isCompact ? 92 : 180,
        bottom: isCompact ? 48 : 50,
        left: isCompact ? 48 : 60,
      };
      const width = Math.max(1, chartOuterWidth - margin.left - margin.right);
      const chartHeight = isCompact ? 420 : 600;
      const height = chartHeight - margin.top - margin.bottom;
      const speedAxisOffset = isCompact ? 44 : 60;
      const xTickCount = isCompact ? 5 : 8;
      const yTickCount = isCompact ? 5 : 10;

      const svg = d3.select(container)
        .append('svg')
        .attr('width', chartOuterWidth)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // Tanpa ini garis meluber ke luar kotak pas sumbu dikunci.
      const clipId = `tk-klip-${this._uid}`;
      svg.append('defs').append('clipPath').attr('id', clipId)
        .append('rect')
        .attr('width', width)
        .attr('height', height);
      const dataLayer = svg.append('g').attr('clip-path', `url(#${clipId})`);

      // X Axis
      const x = d3.scaleTime().domain(this.tkXDomain(timestamps)).range([0, width]);
      svg.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).ticks(xTickCount).tickFormat((d) => formatDateTime(d, CHART_AXIS, null, false)))
        .selectAll('text')
        .style('text-anchor', 'end')
        .attr('dx', '-.8em')
        .attr('dy', '.15em')
        .attr('transform', 'rotate(-45)');

      // -- Scales Setup --

      // Semua domain dihitung dulu, baru garis nolnya disejajarkan — kalau
      // dipasang satu per satu, sumbu yang duluan gak tahu ada yang minus.
      const rpmPeakAll = visible.filter((d) => d.axis === 'rpm')
        .reduce((acc, d) => (d.peak ? acc.concat(d.peak) : acc), []);
      const [domainFuel, domainRpm, domainSpeed] = this.tkAlignZero([
        this.tkDomain('fuel', visible, 10),
        this.tkDomain('rpm', visible, 1000, rpmPeakAll),
        this.tkDomain('speed', visible, 20),
      ]);

      // 1. Fuel (Left Axis) - Main
      const yFuel = d3.scaleLinear().domain([domainFuel[0] * 1.1, domainFuel[1] * 1.1]).range([height, 0]);

      svg.append('g').call(d3.axisLeft(yFuel).ticks(yTickCount))
        .append('text').attr('fill', '#000')
        .attr('transform', 'rotate(-90)')
        .attr('y', isCompact ? -34 : -40)
        .attr('dy', '0.71em')
        .attr('text-anchor', 'end')
        .text('Fuel (L/h)');

      // 2. RPM (Right Axis 1) — hanya digambar kalau ada dataset RPM.
      const hasRpmAxis = visible.some((d) => d.axis === 'rpm');
      const maxRpm = domainRpm[1];
      const yRpm = d3.scaleLinear()
        .domain([domainRpm[0], (Math.ceil(maxRpm / 100) * 100) + 100]).range([height, 0]);

      if (hasRpmAxis) {
        svg.append('g').attr('transform', `translate(${width}, 0)`).call(d3.axisRight(yRpm).ticks(yTickCount))
          .append('text')
          .attr('fill', '#000')
          .attr('transform', 'rotate(-90)')
          .attr('y', 30)
          .attr('dy', '0.71em')
          .attr('text-anchor', 'end')
          .text('RPM');
      }

      // 3. Speed (Right Axis 2) — tanpa sumbu RPM, geser ke posisi sumbu kanan pertama.
      const ySpeed = d3.scaleLinear()
        .domain([domainSpeed[0], domainSpeed[1] * 1.1]).range([height, 0]);
      const speedOffset = hasRpmAxis ? speedAxisOffset : 0;

      this.tkCurrentDomains = { fuel: domainFuel, rpm: domainRpm, speed: domainSpeed };
      // Ekspor ngikut yang keliatan: kena zoom dan seri yang disembunyiin.
      const [xAwal, xAkhir] = x.domain();
      const visibleIndices = [];
      timestamps.forEach((t, i) => {
        if (t >= xAwal && t <= xAkhir) visibleIndices.push(i);
      });
      this.tkLast = {
        timestamps: visibleIndices.map((i) => timestamps[i]),
        datasets: visible.map((d) => ({ name: d.name, data: visibleIndices.map((i) => d.data[i]) })),
      };

      svg.append('g').attr('transform', `translate(${width + speedOffset}, 0)`).call(d3.axisRight(ySpeed).ticks(yTickCount))
        .append('text')
        .attr('fill', '#F59E0B')
        .attr('transform', 'rotate(-90)')
        .attr('y', 30)
        .attr('dy', '0.71em')
        .attr('text-anchor', 'end')
        .text('Speed (kn)');

      // Grid (Based on Fuel Axis)
      svg.append('g').attr('class', 'grid').attr('opacity', 0.1).call(d3.axisLeft(yFuel).ticks(yTickCount).tickSize(-width).tickFormat(''));

      // Garis nol biar batas konsumsi sama aliran balik kebaca.
      if (domainFuel[0] < 0) {
        dataLayer.append('line')
          .attr('x1', 0)
          .attr('x2', width)
          .attr('y1', yFuel(0))
          .attr('y2', yFuel(0))
          .attr('stroke', '#9CA3AF')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '2 2');
      }

      // Helper to select scale
      const getScale = (axisName) => {
        if (axisName === 'fuel') return yFuel;
        if (axisName === 'rpm') return yRpm;
        return ySpeed;
      };

      // Pita puncak: jarak dari rata-rata ke nilai tertinggi di tiap bucket.
      visible.forEach((series) => {
        if (!series.peak) return;
        const peakScale = getScale(series.axis);
        // Diputus lewat defined biar area ga nyambung lintas jeda.
        const points = series.interpolated
          .map((value, i) => ({ i, base: value, peak: series.peak[i] }));
        const hasPeak = (d) => d.base !== null && d.peak !== null && d.peak > d.base;
        dataLayer.append('path')
          .datum(points)
          .attr('fill', series.color)
          .attr('opacity', 0.18)
          .style('pointer-events', 'none')
          .attr('d', d3.area()
            .defined(hasPeak)
            .x((d) => x(timestamps[d.i]))
            .y0((d) => peakScale(d.base))
            .y1((d) => peakScale(d.peak)));
      });

      // Draw Lines & Dots
      const realLineWidth = 2.5;
      const hoverLineWidth = 4;
      const interpolatedLineWidth = 1.5;
      visible.forEach((series, index) => {
        const yScale = getScale(series.axis);

        // Line Interpolated (Dashed)
        dataLayer.append('path')
          .datum(series.interpolated)
          .attr('fill', 'none').attr('stroke', series.color)
          .attr('stroke-width', interpolatedLineWidth)
          .attr('stroke-dasharray', '4 4')
          .attr('opacity', 0.6)
          .attr('d', d3.line().defined((d) => d !== null).x((d, i) => x(timestamps[i])).y((d) => yScale(d)));

        // Line Real (Solid)
        dataLayer.append('path')
          .datum(series.data)
          .attr('fill', 'none').attr('stroke', series.color)
          .attr('stroke-width', realLineWidth)
          .attr('d', d3.line().defined((d) => d !== null).x((d, i) => x(timestamps[i])).y((d) => yScale(d)))
          .on('mouseover', function handleMouseOver() {
            d3.select(this).attr('stroke-width', hoverLineWidth);
          })
          .on('mouseout', function handleMouseOut() {
            d3.select(this).attr('stroke-width', realLineWidth);
          });

        // Dots for Real Data
        dataLayer.selectAll(`.dot-series-${index}`)
          .data(series.data.map((d, i) => ({ value: d, date: timestamps[i] })).filter((d) => d.value !== null))
          .enter()
          .append('circle')
          .attr('class', `dot-series-${index}`)
          .attr('cx', (d) => x(d.date))
          .attr('cy', (d) => yScale(d.value))
          .attr('r', 3)
          .attr('fill', series.color)
          .style('pointer-events', 'none');
      });

      // Tooltip & Hover Interaction
      const tooltip = d3.select(this.$refs.tooltip);
      const bisect = d3.bisector((d) => d).left;

      const focusGroup = svg.append('g').style('display', 'none');

      const focusLine = focusGroup.append('line')
        .attr('stroke', '#aaa').attr('stroke-dasharray', '3 3').attr('y1', 0)
        .attr('y2', height);

      this.tkOnHover((ms) => {
        const [first, last] = x.domain();
        if (ms === null || ms < +first || ms > +last) {
          focusGroup.style('display', 'none');
          return;
        }
        focusGroup.style('display', null);
        focusLine.attr('x1', x(new Date(ms))).attr('x2', x(new Date(ms)));
      });

      const focusCircles = visible.map((ds) => focusGroup.append('circle').attr('r', 5).attr('fill', ds.color).attr('stroke', '#fff')
        .attr('stroke-width', 2));

      const legendSpacing = 18;
      const legendItemWidth = 160;
      const legendColumns = Math.max(1, Math.floor(width / legendItemWidth));
      const legendWidth = Math.min(datasets.length, legendColumns) * legendItemWidth;
      const legendGroup = svg.append('g')
        .attr('class', 'chart-legend')
        .attr('transform', `translate(${Math.max(0, (width - legendWidth) / 2)}, -10)`);
      const legendItem = legendGroup.selectAll('g')
        .data(datasets)
        .enter()
        .append('g')
        .attr('transform', (d, i) => {
          const col = i % legendColumns;
          const row = Math.floor(i / legendColumns);
          return `translate(${col * legendItemWidth}, ${row * legendSpacing})`;
        })
        .style('cursor', 'pointer')
        .on('click', (event, d) => {
          this.tkToggleSeries(d.name);
        });

      legendItem.append('rect')
        .attr('width', 12)
        .attr('height', 12)
        .attr('y', -10)
        .attr('rx', 2)
        .attr('ry', 2)
        .attr('fill', (d) => d.color);

      legendItem.append('text')
        .attr('x', 18)
        .attr('y', 0)
        .attr('dy', '0.32em')
        .style('font-size', '12px')
        .text((d) => d.name);

      legendItem.select('rect').attr('opacity', (d) => (this.hiddenSeries[d.name] ? 0.3 : 1));
      legendItem.select('text')
        .attr('opacity', (d) => (this.hiddenSeries[d.name] ? 0.4 : 1))
        .style('text-decoration', (d) => (this.hiddenSeries[d.name] ? 'line-through' : 'none'));

      const hoverBase = this.tkInstallBrush(svg, x, width, height);
      hoverBase
        .on('mouseover', () => { tooltip.style('opacity', 1); focusGroup.style('display', null); })
        .on('mouseout', () => { tooltip.style('opacity', 0); focusGroup.style('display', 'none'); this.tkPublishHover(null); })
        .on('mousemove', (event) => {
          const x0 = x.invert(d3.pointer(event)[0]);
          const i = bisect(timestamps, x0, 1);
          // eslint-disable-next-line no-nested-ternary
          const index = i < timestamps.length && i > 0 ? (x0 - timestamps[i - 1] > timestamps[i] - x0 ? i : i - 1) : 0;
          const date = timestamps[index];
          const xPos = x(date);

          focusLine.attr('x1', xPos).attr('x2', xPos);
          this.tkPublishHover(+date);

          visible.forEach((ds, idx) => {
            const val = ds.data[index];
            const yScale = getScale(ds.axis);
            if (val !== null && val !== undefined) {
              focusCircles[idx].attr('cx', xPos).attr('cy', yScale(val)).style('display', null);
            } else {
              focusCircles[idx].style('display', 'none');
            }
          });

          let html = `<strong>${formatDateTime(date, CHART_TOOLTIP)}</strong><br/>`;
          visible.forEach((ds) => {
            const val = ds.data[index];
            if (val !== null && val !== undefined) {
              html += `<div style="display:flex; align-items:center; gap:5px;">
                          <span style="width:10px; height:10px; border-radius:50%; background:${ds.color}; display:inline-block;"></span>
                          ${ds.name}: ${val}
                        </div>`;
            }
          });

          const [posX, posY] = d3.pointer(event, container);
          let left = posX + 15;
          const top = posY + 15;
          // Prevent tooltip going off-screen on the right
          if (left + 160 > chartOuterWidth) left = posX - 170;

          tooltip.html(html).style('left', `${left}px`).style('top', `${top}px`);
        });
    },
  },
};
</script>

<style scoped>
.d3-chart-wrapper { font-family: inherit; background: #fff; }
/* d3-axis nempelin font-family="sans-serif" di grup axis; CSS menang atas atribut itu. */
.d3-chart-wrapper ::v-deep [font-family] { font-family: inherit; }
.chart-container { width: 100%; }

@media screen and (max-width: 575px) {
  .d3-chart-wrapper {
    min-width: 720px;
  }
}
</style>
